<template>
  <div class="hr-field-signature">
    <div class="hr-field-signature__title">
      {{ modelValue.docSignatureEmbedValue.providerData.title }}
    </div>
    <img
      class="hr-field-signature__image"
      src="../assets/images/sample-docsignature-template.png"
      alt="document preview"
      :class="{
        'hr-field-signature__image-cursor': !isSigned(),
      }"
      @click="openEmbed"
    />
    <div v-if="!isSigned()">
      <co-button class="hr-field-signature__link" @click="openEmbed">
        {{ t('hr-field-signature:fill-document') }}
      </co-button>
    </div>

    <div v-else class="hr-field-signature__completed-label">
      <div class="hr-field-icon-container hr-field-icon-container--success">
        <svg class="hr-field-icon" aria-hidden="true">
          <use href="../../static/icons/check.svg#check-icon"></use>
        </svg>
      </div>
      {{ t('hr-field-signature:completed') }}
    </div>
    <div v-if="errorMsg">
      <div class="hr-field-icon-container--error">
        <svg class="hr-field-icon" aria-hidden="true">
          <use href="../../static/icons/triangle.svg#triangle-icon"></use>
        </svg>
        <div class="hr-field-error-message">
          {{ errorMsg }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import cloneObj from '@/core/cloneObj';
import { ref } from 'vue';
import { getDocSignatureURL } from '@/core/svc-client/document-signature';
import getHelloSignClient from '@/core/svc-client/hellosign-embed';
import store from '@/store';
import { COUNTRY } from '@/core/country';
import t from '@/lib/i18next';
import { logWebHrError } from '@/core/logger/log-wrapper';
import { checkSignatureStatus } from '@/lib/check-signature-status';

export default {
  name: 'hr-field-signature',
  props: {
    modelValue: {
      type: Object,
      default: () => ({ help: { hint: 'document signature' } }),
    },
  },
  setup(props, { emit }) {
    const stateModule = store.state;
    const {
      tenantSettings: { docSignatureClientId },
      tenantId,
    } = stateModule;
    const hsClient = getHelloSignClient(
      stateModule.tenantSettings ? docSignatureClientId : ''
    );
    const MILLISECONDS_PER_MINUTE = 1000 * 60;
    const REFRESH_INTERVAL = MILLISECONDS_PER_MINUTE * 50;
    const errorMsg = ref('');
    const url = ref('');
    const counter = ref(0);
    const countryCode = store.state.onboarding?.countryCode || COUNTRY.AU;

    const viewport = document.querySelector('meta[name=viewport]');
    const initialViewportContent = viewport?.getAttribute('content') || '';

    if (!isSigned()) {
      reloadDocSignatureUrl();
      const timer = setInterval(async () => {
        if (isSigned() || counter.value > 3) {
          clearInterval(timer);
        } else {
          await reloadDocSignatureUrl();
          counter.value++;
        }
      }, REFRESH_INTERVAL);
    }

    function blockZooming() {
      if (viewport) {
        let content = viewport.getAttribute('content') || '';
        const newContent = content.split(/,\s?/);

        // Prevent browsers from automatically zooming into text fields
        if (!content.includes('maximum-scale=1')) {
          newContent.push('maximum-scale=1');
          content = newContent.join(', ');
        }

        if (content !== viewport.getAttribute('content')) {
          viewport.setAttribute('content', content);
        }
      }
    }

    async function openEmbed() {
      try {
        if (!isSigned()) {
          // We need to temporarily block zooming because Dropbox sign expectation
          // is to have them blocked.
          // Note: their logic is handling zooming within the signing documentation.
          blockZooming();
          hsClient.open(url.value, {
            skipDomainVerification:
              process.env.NODE_ENV !== 'production' ? true : false,
          });
        }
      } catch (e) {
        logWebHrError({
          message: 'Failed to open hellosign embed',
          source: 'hr-field-signature',
          metadata: {
            hasUrl: !!url.value,
            skipDomainVerification:
              process.env.NODE_ENV !== 'production' ? true : false,
          },
          error: e,
        });
        errorMsg.value = e;
      }
    }

    async function reloadDocSignatureUrl() {
      try {
        emit('loading-start');
        const { signingUrl } = await getDocSignatureURL(
          tenantId,
          props.modelValue.docSignatureEmbedValue.providerData.signatureID
        );
        url.value = signingUrl;
      } catch (error) {
        errorMsg.value =
          'Something went wrong to load signature form. Please try again later.';
        logWebHrError({
          message: 'Failed to reload document signature URL',
          source: 'hr-field-signature',
          error,
        });
      } finally {
        emit('loading-end');
      }
    }

    async function updateUserSignedStatus() {
      const newValue = cloneObj(props.modelValue);
      newValue.docSignatureEmbedValue.providerData.signed = 'true';

      if (countryCode === COUNTRY.US) {
        const url = `${tenantId}/${stateModule.Employee.employee.id}/${props.modelValue.id}`;
        newValue.docSignatureEmbedValue.providerData.url = url;
      }

      emit('input', newValue);
    }

    function isSigned() {
      const providerData =
        props.modelValue?.docSignatureEmbedValue?.providerData || {};
      return checkSignatureStatus(providerData);
    }

    let hasBeenSigned = false;
    hsClient.on('sign', async () => {
      await updateUserSignedStatus();
      hasBeenSigned = true;
    });

    hsClient.on('finish', async () => {
      try {
        await updateUserSignedStatus();
      } catch (e) {
        logWebHrError({
          message: 'Failed to update user signature status',
          source: 'hr-field-signature',
          error: e,
        });
        errorMsg.value = e;
      }
    });

    hsClient.on('error', async () => {
      errorMsg.value = 'Something wrong has happened';
    });

    function resetZooming() {
      viewport?.setAttribute('content', initialViewportContent);
    }

    hsClient.on('close', async () => {
      resetZooming();

      if (hasBeenSigned) {
        return;
      }
      await reloadDocSignatureUrl();
    });
    return {
      t,
      errorMsg,
      openEmbed,
      isSigned,
    };
  },
};
</script>

<style scoped lang="scss">
.hr-field-signature__title {
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--co-text-primary);
}
.hr-field-signature__image {
  width: 94px;
  height: 121px;
  margin-bottom: 8px;
  border: 1px solid var(--co-lines-border-transparent);
}
.hr-field-signature__image-cursor {
  cursor: pointer;
}
.hr-field-signature__link {
  color: var(--co-text-highlight);
  margin-top: 8px;
  cursor: pointer;
}
.hr-field-signature__completed-label {
  display: flex;
  align-items: center;
}
.hr-field-icon-container {
  display: inline-block;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  text-align: center;
  justify-content: flex-start;
  vertical-align: middle;
  margin-right: 4px;
}
.hr-field-icon-container--success {
  background-color: var(--co-accent-background-secondary);
}
.hr-field-icon-container--error {
  background-color: #feeded;
  display: inline-flex;
  align-items: center;
  height: 40px;
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  margin-top: 8px;
}
.hr-field-icon {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}
.hr-field-error-message {
  margin-left: 8px;
}
@media only screen and (max-width: 768px) {
  .hr-field-signature {
    font-size: 16px;
  }
}
</style>
